import {useState} from 'react';
import styled, {css} from 'styled-components';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';

interface Props {
	watchCount?: number | null;
	isWatching?: boolean;
	onWatch?: () => void;
	onUnwatch?: () => void;
	watchInFlight?: boolean;
	status: string;
	/**
	 * Retained for caller compatibility. ACC-440: resolve no longer gates on
	 * ticket ownership (it gates on TICKETS_MANAGE), so this is not read here.
	 */
	assigneeId?: string | null;
	onResolve: () => void;
	saving?: boolean;
	/** ACC-441: when provided (caller gates on TICKETS_MANAGE), render a Delete button. */
	onDelete?: () => void;
}

const Bar = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const buttonBase = css`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 6px;
	border: 1px solid;
	padding: 6px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
	cursor: pointer;
	background: #ffffff;
`;
const WatchButton = styled.button<{$watching?: boolean}>`
	${buttonBase};
	border-color: ${p => (p.$watching ? '#bfdbfe' : '#e2e8f0')};
	background: ${p => (p.$watching ? '#eff6ff' : '#ffffff')};
	color: ${p => (p.$watching ? '#1d4ed8' : '#334155')};
	&:hover {
		background: ${p => (p.$watching ? '#dbeafe' : '#f8fafc')};
	}
`;
const WatchIcon = styled.svg<{$watching?: boolean}>`
	width: 16px;
	height: 16px;
	color: ${p => (p.$watching ? '#3b82f6' : '#94a3b8')};
`;
const CountPill = styled.span`
	border-radius: 9999px;
	background: #f1f5f9;
	padding: 2px 6px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #475569;
`;
const ShareButton = styled.button`
	${buttonBase};
	border-color: #e2e8f0;
	color: #334155;
	&:hover {
		background: #f8fafc;
	}
`;
const PlainIcon = styled.svg`
	width: 16px;
	height: 16px;
	color: #94a3b8;
`;
const ResolveButton = styled.button<{$enabled: boolean}>`
	${buttonBase};
	${p =>
		p.$enabled
			? css`
					border-color: #bbf7d0;
					background: #f0fdf4;
					color: #15803d;
					&:hover {
						background: #dcfce7;
					}
			  `
			: css`
					border-color: #e2e8f0;
					background: #f8fafc;
					color: #94a3b8;
					cursor: not-allowed;
					opacity: 0.6;
			  `}
`;
const ResolveIcon = styled.svg`
	width: 16px;
	height: 16px;
`;
const DeleteButton = styled.button`
	${buttonBase};
	border-color: #fecaca;
	color: #dc2626;
	&:hover {
		background: #fef2f2;
	}
`;
const Toast = styled.div`
	position: fixed;
	bottom: 16px;
	right: 16px;
	z-index: 50;
	display: flex;
	align-items: center;
	gap: 12px;
	border-radius: 8px;
	background: #0f172a;
	padding: 12px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #ffffff;
	box-shadow: 0 4px 12px rgb(15 23 42 / 0.1);
`;

export function TicketActionBar({
	watchCount,
	isWatching,
	onWatch,
	onUnwatch,
	watchInFlight,
	status,
	onResolve,
	saving,
	onDelete,
}: Props) {
	const {canManage} = useTicketPerms();
	const [showCopiedToast, setShowCopiedToast] = useState(false);

	async function handleShare() {
		await navigator.clipboard.writeText(window.location.href);
		setShowCopiedToast(true);
		setTimeout(() => setShowCopiedToast(false), 2000);
	}

	const isAlreadyResolved = status === 'resolved' || status === 'closed';

	// ACC-440: resolve is an updateTicket-backed write — gate on TICKETS_MANAGE
	// to mirror the gateway (ACC-468). The legacy "assignee can resolve their own"
	// path is dropped; it would now hit a gateway FORBIDDEN.
	const canResolve = (() => {
		if (isAlreadyResolved) return false;
		return canManage;
	})();

	const resolveTooltip = (() => {
		if (isAlreadyResolved) return 'This ticket is already resolved';
		if (!canResolve) return "You don't have permission to resolve this ticket.";
		return undefined;
	})();

	return (
		<Bar>
			<WatchButton
				type="button"
				onClick={isWatching ? onUnwatch : onWatch}
				disabled={watchInFlight}
				$watching={isWatching}
			>
				<WatchIcon $watching={isWatching} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
					/>
				</WatchIcon>
				{watchInFlight ? '…' : isWatching ? 'Stop Watching' : 'Watch'}
				{watchCount != null && watchCount > 0 && <CountPill>{watchCount}</CountPill>}
			</WatchButton>

			<ShareButton type="button" onClick={handleShare} aria-label="Share">
				<PlainIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
					/>
				</PlainIcon>
				Share
			</ShareButton>

			<ResolveButton
				type="button"
				disabled={!canResolve || saving}
				title={resolveTooltip}
				onClick={canResolve && !saving ? onResolve : undefined}
				$enabled={canResolve && !saving}
				aria-label={resolveTooltip ?? 'Resolve ticket'}
			>
				<ResolveIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
				</ResolveIcon>
				{saving ? 'Resolving…' : 'Resolve'}
			</ResolveButton>

			{onDelete && (
				<DeleteButton type="button" onClick={onDelete} aria-label="Delete ticket">
					<ResolveIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
						/>
					</ResolveIcon>
					Delete
				</DeleteButton>
			)}

			{showCopiedToast && <Toast role="alert">Link copied</Toast>}
		</Bar>
	);
}
